import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { TanStackQueryIds } from '../../query/query-ids'
import { createElemental } from '~~/src/api/elementals'
import type { Elemental, ElementalFormData } from '~~/src/types/elemental'
import { ElementalTypeEnum } from '~~/src/types/elemental'

export interface CreateElementalMutateArgs { elementalType: ElementalTypeEnum; elemental: ElementalFormData }

export const useCreateElemental = () => {
  const queryClient = useQueryClient()
  return useMutation<Awaited<ReturnType<typeof createElemental>>, Error, CreateElementalMutateArgs>({
    mutationKey: ['create-elemental'],
    mutationFn: async ({ elemental }) => {
      return await createElemental(elemental)
    },
    onError: (_, { elementalType }) => {
      queryClient.refetchQueries({
        queryKey: [TanStackQueryIds.PROFILE_LIST_ELEMENTALS, elementalType],
        exact: false,
        type: 'all',
      })
    },
    onSuccess: (result, { elemental, elementalType }) => {
      if (!result)
        return
      useDispatchCustomEvent('elemental:created', { element: result as unknown as Elemental })
      const isList = elementalType === ElementalTypeEnum.List
      if (isList) {
        queryClient.invalidateQueries({
          queryKey: [TanStackQueryIds.PROFILE_LIST_ELEMENTALS, ElementalTypeEnum.List],
          exact: false,
          type: 'all',
        })
        if (elemental.elementalTypeId !== ElementalTypeEnum.List) {
          queryClient.invalidateQueries({
            queryKey: [TanStackQueryIds.PROFILE_LIST_ELEMENTALS, elemental.elementalTypeId],
            exact: false,
            type: 'all',
          })
          queryClient.refetchQueries({
            queryKey: [TanStackQueryIds.PROFILE_LIST_ELEMENTALS, elemental.elementalTypeId],
            exact: false,
            type: 'all',
          })
        }

        return
      }
      if (elemental.listIds && elemental.listIds?.length > 0) {
        for (const listId of elemental.listIds) {
          queryClient.invalidateQueries({
            queryKey: [TanStackQueryIds.LIST_ELEMENTALS, listId],
            exact: false,
            type: 'all',
          })
          queryClient.refetchQueries({
            queryKey: [TanStackQueryIds.LIST_ELEMENTALS, listId],
            exact: false,
            type: 'all',
          })
        }
        return
      }

      const elementalTypeIdOnList = elementalType
      queryClient.invalidateQueries({
        queryKey: [TanStackQueryIds.PROFILE_LIST_ELEMENTALS, elementalTypeIdOnList],
        exact: false,
        type: 'all',
      })
      queryClient.refetchQueries({
        queryKey: [TanStackQueryIds.PROFILE_LIST_ELEMENTALS, elementalTypeIdOnList],
        exact: false,
        type: 'all',
      })
    },
  })
}
